import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlinePlus, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';

const ALL_ASSET_TYPES = [
  { value: 'blog', label: 'Blog Post' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'email_sequence', label: 'Email Sequence' },
  { value: 'one_pager', label: 'One-Pager' },
  { value: 'banner', label: 'Banner' },
  { value: 'service_page', label: 'Service Page' },
  { value: 'presentation', label: 'Presentation' },
  { value: 'marketing_strategy', label: 'Marketing Strategy' },
];

export default function AdminTopics() {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', assetTypes: [] });
  const { addToast } = useToast();

  const loadTopics = () => {
    api.get('/topics').then(({ data }) => {
      setTopics(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { loadTopics(); }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/topics/${editing}`, form);
        addToast('Topic updated');
      } else {
        await api.post('/topics', form);
        addToast('Topic created');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ title: '', description: '', assetTypes: [] });
      loadTopics();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to save topic', 'error');
    }
  };

  const handleEdit = (topic) => {
    setForm({ title: topic.title, description: topic.description, assetTypes: topic.assetTypes });
    setEditing(topic.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this topic?')) return;
    try {
      await api.delete(`/topics/${id}`);
      addToast('Topic deleted');
      loadTopics();
    } catch (err) {
      addToast('Failed to delete topic', 'error');
    }
  };

  const toggleAssetType = (type) => {
    setForm(prev => ({
      ...prev,
      assetTypes: prev.assetTypes.includes(type)
        ? prev.assetTypes.filter(t => t !== type)
        : [...prev.assetTypes, type]
    }));
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-v2-blue"></div></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Topics</h1>
          <p className="text-gray-500">Predefined topics for asset generation</p>
        </div>
        <button onClick={() => { setShowForm(true); setEditing(null); setForm({ title: '', description: '', assetTypes: [] }); }}
          className="flex items-center gap-2 px-4 py-2 bg-v2-blue text-white rounded-lg font-medium hover:bg-blue-700">
          <HiOutlinePlus className="w-5 h-5" /> Add Topic
        </button>
      </div>

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-semibold mb-4">{editing ? 'Edit Topic' : 'Create Topic'}</h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" required value={form.title} onChange={e => setForm({...form, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea required rows={3} value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-v2-blue outline-none resize-vertical" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Available for Asset Types</label>
                <div className="flex flex-wrap gap-2">
                  {ALL_ASSET_TYPES.map(type => (
                    <button key={type.value} type="button" onClick={() => toggleAssetType(type.value)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        form.assetTypes.includes(type.value) ? 'bg-v2-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={form.assetTypes.length === 0} className="flex-1 bg-v2-blue text-white py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
                  {editing ? 'Update' : 'Create'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Topics list */}
      <div className="space-y-3">
        {topics.map(topic => (
          <div key={topic.id} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">{topic.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{topic.description}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {topic.assetTypes.map(type => (
                    <span key={type} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                      {ALL_ASSET_TYPES.find(t => t.value === type)?.label || type}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 ml-4">
                <button onClick={() => handleEdit(topic)} className="text-gray-400 hover:text-v2-blue"><HiOutlinePencil className="w-5 h-5" /></button>
                <button onClick={() => handleDelete(topic.id)} className="text-gray-400 hover:text-red-600"><HiOutlineTrash className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
